import _ from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import {
	Container,
	Input,
	Jumbo,
	MultiSelect,
	Paragraph,
	Select,
	Separator,
	Spinner,
	Subheading,
} from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import { useWorkspace } from 'packages/kakapo-ui-services/hooks/useWorkspace';

function WorkspaceStartDate(props: { startDate?: Date }) {
	const workspace = useWorkspace();
	const [startDate, setStartDate] = useState(props.startDate);

	const [updateStartDate, { loading }] = graphql.useUpdateWorkspaceStartDateMutation();

	return (
		<Flex alignItems="center">
			<Box width={200} mr={2}>
				<Input.DatePicker
					value={startDate}
					onChange={(startDate) => {
						setStartDate(startDate);

						updateStartDate({
							variables: { input: { workspaceID: workspace.id, startDate } },
						});
					}}
				/>
			</Box>
			{loading && <Spinner />}
		</Flex>
	);
}

function WorkspaceLanguages(props: { languageIDs: string[] }) {
	const workspace = useWorkspace();
	const { t } = useTranslation();

	const [languageIDs, setLanguageIDs] = useState(props.languageIDs);

	const [updateLanguages, { loading }] = graphql.useUpdateWorkspaceLanguagesMutation();

	const options = _.sortBy(
		['nl_NL', 'en_US', 'fr_FR', 'de_DE'].map((value) => ({
			title: t(`common.language.${value}`),
			value,
		})),
		'title',
	);

	return (
		<Flex alignItems="center">
			<Box mr={2}>
				<MultiSelect
					required
					selectedOptions={options.filter(({ value }) => languageIDs.includes(value))}
					options={options}
					onChange={(languageIDs) => {
						setLanguageIDs(languageIDs);

						updateLanguages({
							variables: { input: { workspaceID: workspace.id, languageIDs } },
						});
					}}
				/>
			</Box>
			{loading && <Spinner />}
		</Flex>
	);
}

function EngagementSettings(props: { type: graphql.EngagementEnpsType }) {
	const { t } = useTranslation();

	const [update, { loading }] = graphql.useUpdateEngagementSettingsMutation({});

	if (loading) {
		return <Select.Skeleton />;
	}

	return (
		<Select
			search={false}
			onChange={(value) => {
				update({ variables: { input: { enpsType: value as graphql.EngagementEnpsType } } });
			}}
			selectedValue={props.type}
			options={[
				{
					title: t('settings.advanced.enps.european.title'),
					value: graphql.EngagementEnpsType.European,
				},
				{
					title: t('settings.advanced.enps.american.title'),
					value: graphql.EngagementEnpsType.American,
				},
			]}
		/>
	);
}

export default function AdvancedSettings() {
	const { t } = useTranslation();

	const workspace = useWorkspace();
	const query = graphql.useAdvancedWorkspaceSettingsQuery({ variables: { id: workspace.id } });

	return (
		<>
			<Jumbo
				title={t('settings.advanced.title')}
				description={t('settings.advanced.description')}
				wide={1080}
			/>
			<Separator />
			<Container wide={1080} py={4}>
				<Flex alignItems="center">
					<Box width={300} mr={3}>
						<Subheading>{t('settings.advanced.startDate.title')}</Subheading>
						<Paragraph>{t('settings.advanced.startDate.description')}</Paragraph>
					</Box>
					{query.data ? (
						<WorkspaceStartDate
							startDate={
								typeof query.data.workspace.startDate === 'string'
									? new Date(query.data.workspace.startDate)
									: undefined
							}
						/>
					) : (
						<Box width={200}>
							<Input.Skeleton />
						</Box>
					)}
				</Flex>
				<Flex alignItems="center" mt={4}>
					<Box width={300} mr={3}>
						<Subheading>{t('settings.advanced.languages.title')}</Subheading>
						<Paragraph>{t('settings.advanced.languages.description')}</Paragraph>
					</Box>
					{query.data ? (
						<WorkspaceLanguages languageIDs={query.data.workspace.supportedLanguageIDs!} />
					) : (
						<Box width={200}>
							<Input.Skeleton />
						</Box>
					)}
				</Flex>
				<Flex alignItems="center" mt={4}>
					<Box width={300} mr={3}>
						<Subheading>{t('settings.advanced.enps.title')}</Subheading>
						<Paragraph>{t('settings.advanced.enps.description')}</Paragraph>
					</Box>
					{query.data ? (
						<Box width={200}>
							<EngagementSettings type={query.data.workspace.engagementSettings.enpsType} />
						</Box>
					) : (
						<Box width={200}>
							<Select.Skeleton />
						</Box>
					)}
				</Flex>
			</Container>
		</>
	);
}
